import React from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge, ProgressBar } from './ui';
import {
  derivedAssignmentStatus,
  exercisePassed,
  getAfterLessonQuizzes,
  getAssignmentProgress,
  getCourseTrainers,
  getExercisesForModule,
  getLatestExerciseSubmission,
  getLatestQuizAttempt,
  getLessonsForModule,
  getQuizAvailability,
  getModuleEndQuizzes,
  getModulesForVersion,
  quizPassed,
  trainerNames
} from '../utils/lmsSelectors';

function PreviewStatus({ status }) {
  return <StatusBadge status={status || 'not_started'} />;
}

function PreviewQuiz({ data, assignment, quiz, placementNote }) {
  const latest = assignment ? getLatestQuizAttempt(assignment, quiz.id) : null;
  const attemptsUsed = assignment ? assignment.quizAttempts.filter((attempt) => attempt.quizId === quiz.id).length : 0;
  const savedDraft = assignment?.quizAnswerDrafts?.[quiz.id];
  const hasSavedDraft = savedDraft?.answers && Object.keys(savedDraft.answers).length > 0;
  const availability = assignment ? getQuizAvailability(data, assignment, quiz) : { canTake: true };
  const isLocked = assignment && !availability.canTake;
  const status = assignment ? (quizPassed(assignment, quiz) ? 'passed' : latest?.status || (hasSavedDraft ? 'in_progress' : 'not_started')) : 'not_started';
  const content = (
    <div>
      <strong>{quiz.title}</strong>
      <p className="muted small">{placementNote} • Pass {quiz.passingScore}% • Attempts {attemptsUsed}/{quiz.maxAttempts}{hasSavedDraft ? ' • Saved answers' : ''}{isLocked ? ' • Locked' : ''}</p>
      {isLocked && <p className="muted small">{availability.message}</p>}
    </div>
  );
  return (
    <div className="lesson-row quiz-row">
      {assignment && !isLocked ? <Link className="text-link strong-link" to={`/learner/quiz/${assignment.id}:${quiz.id}`}>{content}</Link> : content}
      <PreviewStatus status={status} />
      {assignment && (isLocked ? <button className="button button-secondary" type="button" disabled>Locked</button> : <Link className="button button-secondary" to={`/learner/quiz/${assignment.id}:${quiz.id}`}>Open quiz</Link>)}
    </div>
  );
}

function PreviewExercise({ assignment, exercise }) {
  const latest = assignment ? getLatestExerciseSubmission(assignment, exercise.id) : null;
  const status = assignment ? (exercisePassed(assignment, exercise) ? 'passed' : latest?.status || 'not_started') : 'not_started';
  const content = (
    <div>
      <strong>{exercise.title}</strong>
      <p className="muted small">Graded exercise • Pass {exercise.passingScore}% • Max score {exercise.maxScore}</p>
      {exercise.exerciseFileUrl && <p className="small link-line">Exercise file: <a href={exercise.exerciseFileUrl} target="_blank" rel="noreferrer">{exercise.exerciseFileTitle || exercise.exerciseFileUrl}</a></p>}
      {exercise.instructions && <p className="muted small">{exercise.instructions}</p>}
    </div>
  );
  return (
    <div className="lesson-row quiz-row exercise-row">
      {assignment ? <Link className="text-link strong-link" to={`/learner/exercise/${assignment.id}:${exercise.id}`}>{content}</Link> : content}
      <PreviewStatus status={status} />
      {assignment && <Link className="button button-secondary" to={`/learner/exercise/${assignment.id}:${exercise.id}`}>Open exercise</Link>}
    </div>
  );
}

export default function CoursePreview({ data, course, assignment = null, mode = 'preview' }) {
  if (!course) return <p className="muted">Course not found.</p>;
  const courseVersionId = assignment?.courseVersionId || course.currentVersionId;
  const modules = getModulesForVersion(data, courseVersionId);
  const trainers = getCourseTrainers(data, course);
  const progress = assignment ? getAssignmentProgress(data, assignment) : null;
  const isLearner = mode === 'learner' && assignment;

  return (
    <div className="learner-preview-shell">
      <div className="course-freeze-pane preview-pane">
        <div>
          <p className="eyebrow">Learner view</p>
          <h1>{course.title}</h1>
          {course.description && <p className="muted max-width">{course.description}</p>}
          <p className="muted small">Trainer{trainers.length === 1 ? '' : 's'}: {trainerNames(data, course)}</p>
        </div>
        {assignment && <StatusBadge status={derivedAssignmentStatus(data, assignment)} />}
      </div>

      {assignment && (
        <div className="preview-metrics-row">
          <div><span className="muted small">Progress</span><ProgressBar value={progress.percent} /></div>
          <div><span className="muted small">Due date</span><strong>{assignment.dueDate || 'No due date'}</strong></div>
          <div><span className="muted small">Required items</span><strong>{progress.completed}/{progress.total}</strong></div>
        </div>
      )}

      {modules.map((module) => {
        const moduleEndQuizzes = getModuleEndQuizzes(data, courseVersionId, module.id);
        const moduleExercises = getExercisesForModule(data, courseVersionId, module.id);
        return (
          <div key={module.id} className="module-block learner-style-module">
            <h3>{module.title}</h3>
            {module.description && <p className="muted small">{module.description}</p>}
            <div className="lesson-list">
              {getLessonsForModule(data, module.id).map((lesson) => {
                const lp = assignment?.lessonProgress?.[lesson.id] || { status: 'not_started' };
                const afterLessonQuizzes = getAfterLessonQuizzes(data, courseVersionId, lesson.id);
                const lessonContent = (
                  <div>
                    <strong>{lesson.title}</strong>
                    <p className="muted small">{lesson.lessonType} • {lesson.estimatedMinutes} min</p>
                    {mode !== 'learner' && lesson.contentHtml && <p className="muted small">{lesson.contentHtml}</p>}
                    {lesson.lessonType === 'video' && lesson.videoUrl && <p className="small link-line">Google Drive video: <a href={lesson.videoUrl} target="_blank" rel="noreferrer">{lesson.videoUrl}</a></p>}
                    {lesson.resources?.length > 0 && lesson.lessonType !== 'video' && <p className="small link-line">Resource: <a href={lesson.resources[0].url} target="_blank" rel="noreferrer">{lesson.resources[0].title}</a></p>}
                    {lesson.lessonType === 'practical' && <p className="small link-line">Session: {lesson.sessionDate ? new Date(lesson.sessionDate).toLocaleString() : 'To be announced'}{lesson.meetingLink && <> • <a href={lesson.meetingLink} target="_blank" rel="noreferrer">Meeting link</a></>}</p>}
                  </div>
                );
                return (
                  <React.Fragment key={lesson.id}>
                    <div className="lesson-row">
                      {isLearner ? <Link className="text-link strong-link" to={`/learner/lesson/${assignment.id}:${lesson.id}`}>{lessonContent}</Link> : lessonContent}
                      <PreviewStatus status={assignment ? lp.status : 'not_started'} />
                      {isLearner && <Link className="button button-ghost" to={`/learner/lesson/${assignment.id}:${lesson.id}`}>Open</Link>}
                    </div>
                    {afterLessonQuizzes.map((quiz) => (
                      <PreviewQuiz key={`${quiz.id}-after-${lesson.id}`} data={data} assignment={assignment} quiz={quiz} placementNote="After lesson quiz" />
                    ))}
                  </React.Fragment>
                );
              })}
              {moduleEndQuizzes.length > 0 && <p className="module-end-label">End of module quiz</p>}
              {moduleEndQuizzes.map((quiz) => (
                <PreviewQuiz key={`${quiz.id}-module-end`} data={data} assignment={assignment} quiz={quiz} placementNote={quiz.placement === 'both' ? 'Also shown at module end' : 'Module end quiz'} />
              ))}
              {moduleExercises.length > 0 && <p className="module-end-label">Graded exercise</p>}
              {moduleExercises.map((exercise) => <PreviewExercise key={exercise.id} assignment={assignment} exercise={exercise} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
